/**
 * LegReadingRow (DC-1492)
 *
 * Single leg row inside a PillarCard. Renders one of three states — score
 * plus coverage chip, or one of the two "no score" affordances — keeping the
 * "score never without coverage" contract from the design doc.
 *
 *   present_and_scored   → bar to score on a shared 0–5 scale + CoverageChip
 *   deliberately_absent  → hatched track + "Deliberately absent" (v1 doesn't
 *                          emit, but the renderer handles it for forward compat)
 *   not_yet_gathered     → hatched track + "Not gathered" + CoverageChip
 *
 * Coverage is always shown — even in `not_yet_gathered` so the consultant
 * knows whether the issue is "no signal at all" or "below floor."
 *
 * Editorial redesign (2026-05-26): the three rows now render as bars on a
 * shared 0–5 scale so agreement/divergence across legs is visible at a glance
 * (the core triangulation read). Each row also names its evidence source —
 * the provenance that was previously invisible. data-testid / data-leg /
 * data-state and the "Not gathered" / "Deliberately absent" text contract are
 * unchanged.
 */

import React from 'react';
import type { LegReading } from '../../types/triangulation';
import { CoverageChip } from './CoverageChip';

interface LegReadingRowProps {
  /** Single-letter leg label: 'T' (Technical) / 'P' (Perceived) / 'G' (Governed). */
  shortLabel: 'T' | 'P' | 'G';
  /** Full leg name for screen readers + hover state. */
  fullLabel: string;
  reading: LegReading;
}

/** What each leg actually is — the provenance the score rolls up from. */
const LEG_SOURCE: Record<'T' | 'P' | 'G', React.ReactNode> = {
  T: <>FAIR Live Checks + SHACL</>,
  P: (
    <>
      Organizational Interviews · <b>persona-weighted</b>
    </>
  ),
  G: (
    <>
      Governance Document Lens · <b>8 frameworks</b>
    </>
  ),
};

const SCALE_MAX = 5;

export const LegReadingRow: React.FC<LegReadingRowProps> = ({
  shortLabel,
  fullLabel,
  reading,
}) => {
  const { state, score, coverage } = reading;
  const scored = state === 'present_and_scored' && score !== null;
  const fillPct = scored ? Math.max(0, Math.min(100, (score / SCALE_MAX) * 100)) : 0;

  return (
    <div data-testid="leg-reading-row" data-leg={shortLabel} data-state={state} className="tri-leg">
      <div className="tri-leg-head">
        <span className="tri-leg-key" title={fullLabel}>
          {shortLabel} · {fullLabel}
        </span>
        <span className="tri-leg-source">{LEG_SOURCE[shortLabel]}</span>
      </div>

      <div className="tri-leg-bar-row">
        {scored ? (
          <>
            <div className="tri-track">
              <div className="tri-fill" style={{ width: `${fillPct}%` }}>
                {score.toFixed(1)}
              </div>
            </div>
            <span className="tri-leg-score">/ {SCALE_MAX}</span>
            <CoverageChip coverage={coverage} />
          </>
        ) : state === 'deliberately_absent' ? (
          <div className="tri-track empty">
            <span className="tri-empty-label">Deliberately absent (documented as none)</span>
          </div>
        ) : (
          <>
            <div className="tri-track empty">
              <span className="tri-empty-label">Not gathered</span>
            </div>
            <CoverageChip coverage={coverage} />
          </>
        )}
      </div>
    </div>
  );
};
